import { ApiService } from "../../insights-agent/api/ApiService.interface.ts";
import { AppState, Message, ResearchId } from "./AppState.ts";

type UpdateStateFn = (updater: (draft: AppState) => void) => void;
type MergeMessageFn = (
  message: Message,
  addContent: string,
  thought: boolean,
) => void;

/**
 * Creates a message polling manager for streaming operations.
 * Handles polling lifecycle for individual messages during SSE streaming.
 * All content from the streaming session is merged into a SINGLE message.
 */
export function createMessagePollingManager(
  apiService: ApiService,
  updateState: UpdateStateFn,
  mergeMessageContent: MergeMessageFn,
) {
  const activePolls = new Map<string, NodeJS.Timeout>();
  const processedMessageIds = new Set<string>();
  const contentAddedForMessageId = new Set<string>();
  let streamingMessageId: string | null = null;

  const stopPolling = (messageId: string) => {
    const interval = activePolls.get(messageId);
    if (interval) {
      clearInterval(interval);
      activePolls.delete(messageId);
    }
  };

  const stopAllPolling = () => {
    activePolls.forEach(clearInterval);
    activePolls.clear();
  };

  const startPollingForMessage = (
    messageId: string,
    researchId: ResearchId,
  ) => {
    if (activePolls.has(messageId)) return;

    if (!streamingMessageId) {
      streamingMessageId = "streaming-agent-msg";

      updateState((draft) => {
        const messages = draft.messages.items[researchId];
        if (!messages) return;

        const optimisticIndex = messages.findIndex(
          (m) => m.id === "optimistic-agent-msg",
        );
        if (optimisticIndex >= 0) {
          messages.splice(optimisticIndex, 1);
        }

        const pendingIndex = messages.findIndex(
          (m) => m.id === "pending_message",
        );
        if (pendingIndex >= 0) {
          messages.splice(pendingIndex, 1);
        }

        messages.push({
          id: streamingMessageId!,
          value: "",
          thoughts: "",
          sender: "agent",
          create_time: new Date().toISOString(),
          status: "processing",
        });
      });
    }

    const fetchMessage = async () => {
      try {
        const result = await apiService.getMessage({
          researchId: researchId,
          messageId: messageId,
        });

        if (result?.success && result.data) {
          const apiMessage = result.data.message;
          const content = apiMessage.value ?? "";

          if (
            apiMessage.status === "completed" &&
            !contentAddedForMessageId.has(messageId) &&
            content
          ) {
            contentAddedForMessageId.add(messageId);

            updateState((draft) => {
              const messages = draft.messages.items[researchId];
              const streamingMessage = messages?.find(
                (m) => m.id === streamingMessageId,
              );
              if (streamingMessage) {
                mergeMessageContent(
                  streamingMessage,
                  content,
                  apiMessage.thought ?? false,
                );
              }
            });

            stopPolling(messageId);
            processedMessageIds.add(messageId);
          }
        }
      } catch {
        // Don't stop polling on transient errors - let it retry
      }
    };

    fetchMessage();
    const interval = setInterval(fetchMessage, 1000);
    activePolls.set(messageId, interval);
  };

  return {
    startPollingForMessage,
    stopPolling,
    stopAllPolling,
    getStreamingMessageId: () => streamingMessageId,
  };
}
